import { CONFERENCE_JOINED, CONFERENCE_LEFT, CONFERENCE_WILL_LEAVE } from '../base/conference/actionTypes';
import { JitsiConnectionQualityEvents } from '../base/lib-jitsi-meet';
import { getWhitelabelConfig } from '../base/whitelabel/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { getTelemetryObserverId } from '../mobile/navigation/telemetry';
import { flushTelemetryQueue, queueTelemetryEvent } from '../mobile/navigation/telemetryQueue';
import { expectedMediaTransport } from '../visual-language-media-policy/p2pPolicy';
import { countConferencePeople, getActiveTransport } from '../visual-language-media-policy/p2pTransport';
import { getPersistentJson } from '../vrs-auth/storage';
import { markRemoteRenderSampled, refreshRemoteVideoCounters, remoteRenderSummary, remoteVideoCountersFresh, resetRemoteVideoCounters } from './remoteVideoStats';
import statsEmitter from './statsEmitter';
/*
 * Media quality is sampled on change, not on a clock (2026-09-24). Stats
 * arrive every 10 s; a sample is sent when the call starts, when quality
 * moves materially (MEDIA_QUALITY_CHANGE), and otherwise as a heartbeat every
 * MEDIA_QUALITY_HEARTBEAT_MS. A steady call sends one a minute, half the old
 * fixed 30 s pace; a troubled one reports each turn as it happens, so the
 * per-call summary still finds the worst moments.
 *
 * The heartbeat must stay well inside the server's abandoned-room window
 * (ABANDONED_MEDIA_SILENCE_MS, 3 minutes, ws/call-lifecycle.js): samples are
 * also the proof that someone is still in the room.
 */
const MEDIA_QUALITY_HEARTBEAT_MS = 60000;
const MEDIA_QUALITY_MIN_INTERVAL_MS = 10000;
const MEDIA_QUALITY_CHANGE = {
    bitrateRatio: 0.35,
    bitrateMinKbps: 150,
    fpsBands: [10, 18, 24],
    lossBandsPct: [1, 3, 5, 10],
    // Remote render (2026-09-28): frozen, poor, moving. Coarse on purpose, so
    // a weak link's wobble does not multiply samples; a freeze still reports
    // as it happens.
    remoteFpsBands: [1, 10]
};
/*
 * Remote render fields come from inbound-rtp counters. The room's stall
 * monitor refreshes them every few seconds; without it (another layout), the
 * stats tick refreshes them for the next sample.
 */
const REMOTE_RENDER_REFRESH_MS = 8000;
let activeConference;
let lastMediaQualitySampleAt = 0;
let lastMediaQualitySampleKey = '';
let lastMediaQualitySnapshot;
let mediaQualityStatsHandler;
function numericValue(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
function numberFromPath(source, ...path) {
    let value = source;
    for (const key of path) {
        if (!value || typeof value !== 'object') {
            return undefined;
        }
        value = value[key];
    }
    return numericValue(value);
}
function maxNestedNumber(value, key) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return key ? undefined : value;
    }
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    if (key && typeof value[key] === 'number') {
        return numericValue(value[key]);
    }
    return Object.values(value)
        .map(item => maxNestedNumber(item, key))
        .filter((item) => typeof item === 'number' && Number.isFinite(item))
        .reduce((max, item) => Math.max(max, item), 0) || undefined;
}
function getParticipantCount(state) {
    const participants = state['features/base/participants'];
    return (participants?.remote?.size || 0) + 1;
}
function getTelemetryContext(state) {
    const tenant = getWhitelabelConfig();
    const activeCall = getPersistentJson('vrs_active_call');
    const conferenceState = state['features/base/conference'];
    const currentCall = conferenceState.room && activeCall?.roomName === conferenceState.room ? activeCall : undefined;
    return {
        appType: tenant?.operations?.appType,
        callId: currentCall?.callId,
        requestId: currentCall?.requestId,
        roomName: conferenceState.room,
        tenantId: tenant?.tenantId || tenant?.id
    };
}
function band(value, edges) {
    return value === undefined ? -1 : edges.filter(edge => value >= edge).length;
}
function bitrateMoved(previous, next) {
    if (previous === undefined || next === undefined) {
        return previous !== next;
    }
    const delta = Math.abs(next - previous);
    return delta >= MEDIA_QUALITY_CHANGE.bitrateMinKbps
        && delta >= MEDIA_QUALITY_CHANGE.bitrateRatio * Math.max(previous, 1);
}
/** Did quality move enough since the last sample sent to be worth a new one? Exported for tests. */
export function mediaQualityChanged(previous, next) {
    return previous.degraded !== next.degraded
        || previous.participantCount !== next.participantCount
        || previous.expectedBridge !== next.expectedBridge
        || previous.transport !== next.transport
        || previous.localVideoHeight !== next.localVideoHeight
        || band(previous.localFps, MEDIA_QUALITY_CHANGE.fpsBands)
            !== band(next.localFps, MEDIA_QUALITY_CHANGE.fpsBands)
        || band(previous.lossPct, MEDIA_QUALITY_CHANGE.lossBandsPct)
            !== band(next.lossPct, MEDIA_QUALITY_CHANGE.lossBandsPct)
        || band(previous.remoteFps, MEDIA_QUALITY_CHANGE.remoteFpsBands)
            !== band(next.remoteFps, MEDIA_QUALITY_CHANGE.remoteFpsBands)
        || bitrateMoved(previous.bitrateUploadKbps, next.bitrateUploadKbps)
        || bitrateMoved(previous.bitrateDownloadKbps, next.bitrateDownloadKbps);
}
/**
 * Why to send a sample now, or undefined to skip it. Exported for tests.
 */
export function mediaQualitySampleReason(previous, next) {
    if (next.key !== previous.key || !previous.snapshot) {
        return 'first';
    }
    const elapsed = next.at - previous.at;
    if (elapsed < MEDIA_QUALITY_MIN_INTERVAL_MS) {
        return undefined;
    }
    if (mediaQualityChanged(previous.snapshot, next.snapshot)) {
        return 'change';
    }
    return elapsed >= MEDIA_QUALITY_HEARTBEAT_MS ? 'heartbeat' : undefined;
}
/**
 * What the call is on and what it should be on, from the conference's own
 * transport state (visual-language-media-policy/p2pTransport.ts). Exported
 * for tests.
 *
 * - transport: 'p2p' or 'jvb', what the media uses now;
 * - expectedBridge: 'p2p' for two people with direct media allowed, 'jvb' for
 *   any other count (hidden observers included), 'p2p_disabled' for two
 *   people who stay on the bridge;
 * - transportSwitches: moves between the two so far;
 * - p2pOff: why direct media is off here, when it is (p2pPolicy.ts).
 */
export function describeMediaTransport(conference, participantCount) {
    const active = getActiveTransport(conference);
    let p2pActive;
    try {
        const live = conference;
        p2pActive = typeof live?.isP2PActive === 'function' ? live.isP2PActive() === true : undefined;
    }
    catch {
        p2pActive = undefined;
    }
    const transport = p2pActive === undefined
        ? active?.monitor.currentTransport()
        : p2pActive ? 'p2p' : 'jvb';
    const hidden = countConferencePeople(conference)?.hidden ?? 0;
    return {
        expectedBridge: expectedMediaTransport({
            hiddenParticipantCount: hidden,
            p2pEnabled: active?.decision.enabled ?? false,
            participantCount
        }),
        p2pOff: active?.decision.reason,
        transport,
        transportSwitches: active?.monitor.switchCount()
    };
}
function emitMediaQualitySample(state, stats) {
    const now = Date.now();
    const context = getTelemetryContext(state);
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    const surface = nav?.product === 'ReactNative' ? 'mobile' : 'web';
    const sampleKey = [
        surface,
        context.tenantId || '',
        context.callId || '',
        context.requestId || '',
        context.roomName || ''
    ].join(':');
    const participantCount = getParticipantCount(state);
    const bitrateUploadKbps = numberFromPath(stats.bitrate, 'upload');
    const bitrateDownloadKbps = numberFromPath(stats.bitrate, 'download');
    const packetLossUploadPct = numberFromPath(stats.packetLoss, 'upload');
    const packetLossDownloadPct = numberFromPath(stats.packetLoss, 'download');
    const localVideoHeight = maxNestedNumber(stats.resolution, 'height');
    const localVideoWidth = maxNestedNumber(stats.resolution, 'width');
    const localFps = maxNestedNumber(stats.framerate);
    const degraded = Boolean((localVideoHeight && localVideoHeight < 540)
        || (localFps && localFps < 18)
        || (packetLossUploadPct && packetLossUploadPct > 5)
        || (packetLossDownloadPct && packetLossDownloadPct > 5)
        || (bitrateUploadKbps && bitrateUploadKbps < 500));
    const remoteRender = remoteRenderSummary(now);
    const { expectedBridge, p2pOff, transport, transportSwitches } = describeMediaTransport(state['features/base/conference'].conference, participantCount);
    const snapshot = {
        bitrateDownloadKbps,
        bitrateUploadKbps,
        degraded,
        expectedBridge,
        localFps,
        localVideoHeight,
        lossPct: Math.max(packetLossUploadPct ?? 0, packetLossDownloadPct ?? 0),
        participantCount,
        remoteFps: remoteRender?.remoteFps,
        transport
    };
    const sampleReason = mediaQualitySampleReason({
        at: lastMediaQualitySampleAt,
        key: lastMediaQualitySampleKey,
        snapshot: lastMediaQualitySnapshot
    }, {
        at: now,
        key: sampleKey,
        snapshot
    });
    if (!sampleReason) {
        return;
    }
    lastMediaQualitySampleAt = now;
    lastMediaQualitySampleKey = sampleKey;
    lastMediaQualitySnapshot = snapshot;
    markRemoteRenderSampled();
    queueTelemetryEvent({
        appType: context.appType,
        callId: context.callId,
        event: 'media_quality_sample',
        metadata: {
            bitrateDownloadKbps,
            bitrateUploadKbps,
            expectedBridge,
            localFps,
            localVideoHeight,
            localVideoWidth,
            observerId: getTelemetryObserverId(),
            p2pOff,
            packetLossDownloadPct,
            packetLossUploadPct,
            participantCount,
            ...remoteRender,
            sampleReason,
            transport,
            transportSwitches
        },
        platform: surface === 'mobile' ? 'native' : 'web',
        requestId: context.requestId,
        roomName: context.roomName,
        state: degraded ? 'degraded' : 'ok',
        surface,
        tenantId: context.tenantId
    }, { routine: true });
}
function stopMediaQualityTelemetry() {
    if (activeConference && mediaQualityStatsHandler) {
        activeConference.off?.(JitsiConnectionQualityEvents.LOCAL_STATS_UPDATED, mediaQualityStatsHandler);
        activeConference.removeListener?.(JitsiConnectionQualityEvents.LOCAL_STATS_UPDATED, mediaQualityStatsHandler);
    }
    activeConference = undefined;
    mediaQualityStatsHandler = undefined;
    lastMediaQualitySnapshot = undefined;
    resetRemoteVideoCounters();
}
/**
 * Implements the middleware of the feature connection-indicator.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
        case CONFERENCE_JOINED: {
            statsEmitter.startListeningForStats(action.conference);
            stopMediaQualityTelemetry();
            activeConference = action.conference;
            mediaQualityStatsHandler = (stats) => {
                if (activeConference === action.conference
                    && store.getState()['features/base/conference'].conference === action.conference) {
                    emitMediaQualitySample(store.getState(), stats);
                    if (!remoteVideoCountersFresh(Date.now(), REMOTE_RENDER_REFRESH_MS)) {
                        refreshRemoteVideoCounters(action.conference);
                    }
                }
            };
            activeConference?.on?.(JitsiConnectionQualityEvents.LOCAL_STATS_UPDATED, mediaQualityStatsHandler);
            break;
        }
        case CONFERENCE_LEFT:
        case CONFERENCE_WILL_LEAVE: {
            statsEmitter.stopListeningForStats(action.conference);
            if (action.conference === activeConference) {
                stopMediaQualityTelemetry();
                // The call's last samples go now rather than with the next batch.
                void flushTelemetryQueue();
            }
            break;
        }
    }
    return next(action);
});
